import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { Button } from '@chakra-ui/react'
import { REPOSITORY } from '../../api/repository/Repository'
import { useNavigate, useParams } from 'react-router-dom'
import { employeeStatus } from '../../variables/employees'
import { useRequestStatusContext } from '../../utils/hooks/Context/RequestStatusContext'
import { useLoadingContext } from '../../utils/hooks/Context/LoadingContext'
import { format, parse } from 'date-fns'

export const EmployeeDataContext = createContext()

export const useEmployeeDataContext = () => {
  const context = useContext(EmployeeDataContext)
  return context || {}
}

export const useEmployeeData = () => {
  const navigate = useNavigate()
  const onClose = () => {
    navigate(-1)
  }
  const [employee, setEmployee] = useState()
  const params = useParams()
  const [loading, setLoading] = useState(true)
  const handleChangeData = useCallback((event, key) => {
    if (event?.target) {
      setEmployee((prevState) => ({ ...prevState, [key]: event.target.value }))
    } else {
      setEmployee((prevState) => ({ ...prevState, [key]: event }))
    }
  }, [])

  const { handleOpen: handleOpenLoading, handleClose: handleCloseLoading } =
    useLoadingContext()

  const [actionLoading, setActionLoading] = useState(false)

  const { handleOpen, handleSetTitle, handleChangeBody, handleClose } =
    useRequestStatusContext()

  const canDisable = useMemo(
    () =>
      [employeeStatus.active, employeeStatus.in_process].includes(
        employee?.sEstado,
      ),
    [employee],
  )

  const canActivate = useMemo(
    () =>
      [employeeStatus.unactive, employeeStatus.in_process].includes(
        employee?.sEstado,
      ),
    [employee],
  )

  const handleGetEmployee = async () => {
    const data = await REPOSITORY.wsEmpresa.employees.employeeDetails(
      params.sEmpleado,
    )
    setEmployee(data.data)
    setLoading(false)
  }

  const onOkStatus = useCallback(
    (response, title = null) => {
      setActionLoading(false)
      setEmployee((prevState) => ({
        ...prevState,
        ...response.data.cDatosRespuesta,
      }))
      handleSetTitle(title ? title : 'Empleado actualizado', response.status)
      handleChangeBody(
        <Button variant="primary" onClick={handleClose}>
          {response.status === 200 ? 'Continuar' : 'Contactar a soporte ahora'}
        </Button>,
      )
      handleOpen()
    },
    [handleSetTitle, handleChangeBody, handleOpen, handleClose],
  )

  const onError = useCallback(
    (response, title = null) => {
      setActionLoading(false)
      handleSetTitle(
        title ? title : 'Error al editar empleado',
        response.status,
      )
      handleChangeBody(
        <Button variant="primary" onClick={handleClose}>
          {response.status === 200 ? 'Continuar' : 'Contactar a soporte ahora'}
        </Button>,
      )
      handleOpen()
    },
    [handleSetTitle, handleChangeBody, handleOpen, handleClose],
  )
  const sendPin = useCallback(async () => {
    setActionLoading(true)
    await REPOSITORY.wsEmpresa.employees
      .sendSecurityPin(employee.sEmpleado)
      .then((response) => {
        onOkStatus(response, 'Pin enviado a email registrado en el empleado')
      })
  }, [onOkStatus, employee])

  const handleUpdateEmployee = useCallback(
    async (values) => {
      handleOpenLoading()
      const haveDate = values?.dNacimiento

      const brithParsed =
        haveDate && typeof values?.dNacimiento === 'string'
          ? parse(values?.dNacimiento, 'dd-MM-yyyy', new Date())
          : values?.dNacimiento

      const finalBirth = haveDate
        ? { dNacimiento: format(brithParsed, 'yyyy-MM-dd') }
        : {}
      await REPOSITORY.wsEmpresa.employees
        .editEmployee({
          ...values,
          ...finalBirth,
        })
        .then((res) => {
          // onOkStatus(res, res.data.sMensaje)
          onOkStatus(
            res,
            'La información del colaborador ha sido actualizada con éxito.',
          )
          setEmployee(values)
        })
        .catch((error) => {
          onError(error.response)
        })
      handleCloseLoading()
    },
    [handleOpenLoading, handleCloseLoading, onOkStatus, onError, employee],
  )

  const handleChangeState = async () => {
    if (employee?.sEstado !== employeeStatus.unlinked) {
      setActionLoading(true)
      if (canActivate) {
        await changeStatus(employeeStatus.active).then((response) => {
          onOkStatus(response)
        })
      } else {
        await changeStatus(employeeStatus.unactive).then((response) => {
          onOkStatus(response)
        })
      }
    }
  }

  const handleUnlinkEmployee = async () => {
    if (canDisable || employee.sEstado === employeeStatus.unactive) {
      setActionLoading(true)
      await changeStatus(employeeStatus.unlinked).then((response) => {
        onOkStatus(response)
      })
    }
  }

  const changeStatus = async (status) => {
    return REPOSITORY.wsEmpresa.employees.updateStatus(status, [employee])
  }

  useEffect(() => {
    handleGetEmployee()
  }, [])

  useEffect(() => {
    if (loading || actionLoading) {
      handleOpenLoading()
    } else {
      handleCloseLoading()
    }
  }, [loading, actionLoading])

  return {
    employee,
    loading,
    canActivate,
    handleChangeData,
    handleChangeState,
    handleUnlinkEmployee,
    onClose,
    sendPin,
    handleUpdateEmployee,
  }
}
